import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type Category = {
  id: string;
  parent_id: string | null;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  sort_order: number;
  minimum_boost_cents: number;
  minimum_increment_cents: number;
  boost_duration_days: number;
  stage1_enabled: boolean;
  season_number: number;
  season_started_at: string;
  season_ends_at: string | null;
  opening_boost_cents: number;
  opening_banner_enabled: boolean;
};

export type Listing = {
  id: string;
  owner_user_id: string | null;
  listing_type: string;
  name: string;
  slug: string;
  short_code?: string | null;
  headline: string | null;
  short_description: string | null;
  long_description: string | null;
  website_url: string | null;
  country_code: string | null;
  city: string | null;
  logo_url: string | null;
  cover_url: string | null;
  brand_color: string | null;
  tags: string[] | null;
  founded_year: number | null;
  team_size: string | null;
  pricing_note: string | null;
  listing_type_label?: string;
  moderation_status?: string;
  verification_status?: string | null;
  claim_status?: string | null;
  socials: Record<string, string> | null;
  click_count: number;
  created_at: string;
  published_at: string | null;
};

export type BoardRow = {
  listing: Listing;
  active_heat_cents: number;
  booster_count: number;
  top_category_id?: string | null;
};


const CATEGORY_FIELDS =
  "id,parent_id,name,slug,description,icon,sort_order,minimum_boost_cents,minimum_increment_cents,boost_duration_days,stage1_enabled,season_number,season_started_at,season_ends_at,opening_boost_cents,opening_banner_enabled";

const LISTING_FIELDS =
  "id,owner_user_id,listing_type,name,slug,short_code,headline,short_description,long_description,website_url,country_code,city,logo_url,cover_url,brand_color,tags,founded_year,team_size,pricing_note,verification_status,claim_status,socials,click_count,created_at,published_at";

export const categoriesQuery = queryOptions({
  queryKey: ["categories"],
  queryFn: async (): Promise<Category[]> => {
    const { data, error } = await supabase
      .from("categories")
      .select(CATEGORY_FIELDS)
      .order("name", { ascending: true });
    if (error) throw error;
    return (data ?? []) as unknown as Category[];
  },
  staleTime: 5 * 60 * 1000,
});

export function categoryBySlugQuery(slug: string) {
  return queryOptions({
    queryKey: ["category", slug],
    queryFn: async (): Promise<Category | null> => {
      const { data, error } = await supabase
        .from("categories")
        .select(CATEGORY_FIELDS)
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as Category | null;
    },
  });
}

type HeatRow = {
  category_id: string;
  listing_id: string;
  active_heat_cents: number;
  booster_count: number;
  first_boost_at: string;
};

async function fetchHeatRows(categoryIds?: string[]): Promise<HeatRow[]> {
  let q = supabase
    .from("category_board")
    .select("category_id,listing_id,active_heat_cents,booster_count,first_boost_at")
    .order("active_heat_cents", { ascending: false })
    .limit(500);
  if (categoryIds) q = q.in("category_id", categoryIds);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as unknown as HeatRow[];
}

async function boardFromRows(rows: HeatRow[], limit: number): Promise<BoardRow[]> {
  // A parent board aggregates the Heat a listing holds across its sub-boards.
  const totals = new Map<
    string,
    { heat: number; boosters: number; first: string; topCat: string; topCatHeat: number }
  >();
  for (const r of rows) {
    const prev = totals.get(r.listing_id);
    const heat = Number(r.active_heat_cents);
    totals.set(r.listing_id, {
      heat: (prev?.heat ?? 0) + heat,
      boosters: Math.max(prev?.boosters ?? 0, Number(r.booster_count)),
      first: prev && prev.first < r.first_boost_at ? prev.first : r.first_boost_at,
      topCat: !prev || heat > prev.topCatHeat ? r.category_id : prev.topCat,
      topCatHeat: Math.max(prev?.topCatHeat ?? 0, heat),
    });
  }

  const paidIds = [...totals.keys()];
  const { data: paidListings, error: paidError } = paidIds.length
    ? await supabase.from("listings").select(LISTING_FIELDS).in("id", paidIds)
    : { data: [], error: null };
  if (paidError) throw paidError;

  const byId = new Map(((paidListings ?? []) as unknown as Listing[]).map((l) => [l.id, l]));

  return paidIds
    .filter((id) => byId.has(id))
    .map((id) => ({
      listing: byId.get(id)!,
      active_heat_cents: totals.get(id)!.heat,
      booster_count: totals.get(id)!.boosters,
      top_category_id: totals.get(id)!.topCat,
      first_boost_at: totals.get(id)!.first,
    }))
    .sort(
      (a, b) =>
        b.active_heat_cents - a.active_heat_cents ||
        a.first_boost_at.localeCompare(b.first_boost_at),
    )
    .slice(0, limit)
    .map(({ listing, active_heat_cents, booster_count, top_category_id }) => ({
      listing,
      active_heat_cents,
      booster_count,
      top_category_id,
    }));
}

async function fetchBoard(categoryIds: string[], limit = 50): Promise<BoardRow[]> {
  return boardFromRows(await fetchHeatRows(categoryIds), limit);
}

export function categoryBoardQuery(categoryIds: string | string[] | undefined) {
  const ids = typeof categoryIds === "string" ? [categoryIds] : (categoryIds ?? []);
  return queryOptions({
    queryKey: ["board", ids],
    queryFn: () => (ids.length ? fetchBoard(ids) : Promise.resolve([])),
    enabled: ids.length > 0,
  });
}

/** The General board — every listing on the platform ranked by total active Heat. */
export const generalBoardQuery = queryOptions({
  queryKey: ["general-board"],
  queryFn: () => fetchHeatRows().then((rows) => boardFromRows(rows, 25)),
});

/** Persisted rank state of the General board: current position, last position and new entries. */
export type BoardChange = {
  listing_id: string;
  current_rank: number;
  previous_rank: number | null;
  is_new: boolean;
  heat_cents: number;
  moved_at: string | null;
  first_seen_at: string | null;
};

export const generalBoardChangesQuery = queryOptions({
  queryKey: ["general-board-ranks"],
  queryFn: async (): Promise<BoardChange[]> => {
    const { getGeneralBoardRanks } = await import("@/lib/public-helpers.functions");
    return getGeneralBoardRanks();
  },
  staleTime: 15 * 1000,
  refetchOnMount: "always",
  refetchInterval: 60 * 1000,
});


/** A listing's rank on the General board (all boards combined). */
export function listingGeneralRankQuery(listingId: string | undefined) {
  return queryOptions({
    queryKey: ["general-rank", listingId],
    enabled: Boolean(listingId),
    queryFn: async (): Promise<{ rank: number | null; heat_cents: number; total: number }> => {
      if (!listingId) return { rank: null, heat_cents: 0, total: 0 };
      const rows = await fetchHeatRows();
      const totals = new Map<string, number>();
      for (const r of rows) {
        totals.set(r.listing_id, (totals.get(r.listing_id) ?? 0) + Number(r.active_heat_cents));
      }
      const sorted = [...totals.entries()].sort((a, b) => b[1] - a[1]);
      const index = sorted.findIndex(([id]) => id === listingId);
      return {
        rank: index >= 0 ? index + 1 : null,
        heat_cents: totals.get(listingId) ?? 0,
        total: sorted.length,
      };
    },
  });
}

/** Total active Heat per category, used for the category leaderboard. */
export const categoryHeatQuery = queryOptions({
  queryKey: ["category-heat"],
  queryFn: async () => {
    const rows = await fetchHeatRows();
    const totals = new Map<string, { heat: number; listings: number }>();
    for (const r of rows) {
      const prev = totals.get(r.category_id);
      totals.set(r.category_id, {
        heat: (prev?.heat ?? 0) + Number(r.active_heat_cents),
        listings: (prev?.listings ?? 0) + 1,
      });
    }
    return totals;
  },
});

export type BoardOverview = {
  heat: number;
  listings: number;
  boosters: number;
  entryFloor: number;
  top: { listing: Listing; heat: number }[];
};

/** Per-category Heat, listing counts and the current top holders. */
export const boardsOverviewQuery = queryOptions({
  queryKey: ["boards-overview"],
  queryFn: async (): Promise<Map<string, BoardOverview>> => {
    const rows = await fetchHeatRows();
    const ids = [...new Set(rows.map((r) => r.listing_id))];
    const { data, error } = ids.length
      ? await supabase.from("listings").select(LISTING_FIELDS).in("id", ids)
      : { data: [], error: null };
    if (error) throw error;
    const byId = new Map(((data ?? []) as unknown as Listing[]).map((l) => [l.id, l]));

    const out = new Map<string, BoardOverview>();
    for (const r of rows) {
      const listing = byId.get(r.listing_id);
      if (!listing) continue;
      const heat = Number(r.active_heat_cents);
      const prev =
        out.get(r.category_id) ??
        ({ heat: 0, listings: 0, boosters: 0, entryFloor: Infinity, top: [] } as BoardOverview);
      prev.heat += heat;
      prev.listings += 1;
      prev.boosters += Number(r.booster_count ?? 0);
      prev.entryFloor = Math.min(prev.entryFloor, heat);
      prev.top.push({ listing, heat });
      out.set(r.category_id, prev);
    }
    for (const v of out.values()) {
      v.top.sort((a, b) => b.heat - a.heat);
      v.top = v.top.slice(0, 3);
      if (!Number.isFinite(v.entryFloor)) v.entryFloor = 0;
    }
    return out;
  },
});




/** Approved listings on a board with zero active heat — "Discover more". */
export function discoverListingsQuery(categoryIds: string | string[] | undefined) {
  const ids = typeof categoryIds === "string" ? [categoryIds] : (categoryIds ?? []);
  return queryOptions({
    queryKey: ["discover", ids],
    queryFn: async (): Promise<Listing[]> => {
      if (!ids.length) return [];
      const { data, error } = await supabase
        .from("listing_categories")
        .select(`listing:listings(${LISTING_FIELDS})`)
        .in("category_id", ids)
        .limit(30);
      if (error) throw error;
      return ((data ?? []) as unknown as { listing: Listing | null }[])
        .map((r) => r.listing)
        .filter((l): l is Listing => Boolean(l));
    },
    enabled: ids.length > 0,
  });
}

/** A category plus every sub-board beneath it. */
export function boardScopeIds(
  categories: Pick<Category, "id" | "parent_id">[],
  categoryId: string | undefined,
): string[] {
  if (!categoryId) return [];
  const children = categories.filter((c) => c.parent_id === categoryId).map((c) => c.id);
  return [categoryId, ...children];
}


export function listingBySlugQuery(slug: string) {
  return queryOptions({
    queryKey: ["listing", slug],
    queryFn: async (): Promise<Listing | null> => {
      const { data, error } = await supabase
        .from("listings")
        .select(LISTING_FIELDS)
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as Listing | null;
    },
  });
}

/** Boards a listing appears on, used for the boost call-to-action. */
export function listingBoardsQuery(listingId: string | undefined) {
  return queryOptions({
    queryKey: ["listing-boards", listingId],
    enabled: Boolean(listingId),
    queryFn: async (): Promise<{ id: string; name: string; slug: string }[]> => {
      if (!listingId) return [];
      const { data, error } = await supabase
        .from("listing_categories")
        .select("category:categories(id,name,slug)")
        .eq("listing_id", listingId);
      if (error) throw error;
      return ((data ?? []) as unknown as {
        category: { id: string; name: string; slug: string } | null;
      }[])
        .map((r) => r.category)
        .filter((c): c is { id: string; name: string; slug: string } => Boolean(c));
    },
  });
}

/** Counts an outbound click on a listing's website. */
export async function trackListingClick(slug: string) {
  const { recordListingClick } = await import("@/lib/clicks.functions");
  await recordListingClick({ data: { slug } });
}


export const latestListingsQuery = queryOptions({
  queryKey: ["latest-listings"],
  queryFn: async (): Promise<Listing[]> => {
    const { data, error } = await supabase
      .from("listings")
      .select(LISTING_FIELDS)
      .order("created_at", { ascending: false })
      .limit(12);
    if (error) throw error;
    return (data ?? []) as unknown as Listing[];
  },
});

export const hotActivityQuery = queryOptions({
  queryKey: ["hot-activity"],
  queryFn: async () => {
    const { data, error } = await supabase
      .from("boosts_public")
      .select(
        "id,value_cents,created_at,listing_name,listing_slug,category_name,category_slug",
      )
      .order("created_at", { ascending: false })
      .limit(10);
    if (error) throw error;
    return ((data ?? []) as unknown as {
      id: string;
      value_cents: number;
      created_at: string;
      listing_name: string | null;
      listing_slug: string | null;
      category_name: string | null;
      category_slug: string | null;
    }[]).map((r) => ({
      id: r.id,
      value_cents: r.value_cents,
      created_at: r.created_at,
      listing: r.listing_slug ? { name: r.listing_name ?? "", slug: r.listing_slug } : null,
      category: r.category_slug ? { name: r.category_name ?? "", slug: r.category_slug } : null,
    }));
  },
});

/* ------------------------------------------------------------------ */
/* Listing detail                                                      */
/* ------------------------------------------------------------------ */

export type BoostHistoryRow = {
  id: string;
  value_cents: number;
  created_at: string;
  expires_at: string;
  status: string;
  category: { name: string; slug: string } | null;
};

/** Public boost history for a listing. */
export function listingBoostHistoryQuery(listingId: string | undefined) {
  return queryOptions({
    queryKey: ["listing-boosts", listingId],
    enabled: Boolean(listingId),
    queryFn: async (): Promise<BoostHistoryRow[]> => {
      if (!listingId) return [];
      const { data, error } = await supabase
        .from("boosts_public")
        .select("id,value_cents,created_at,expires_at,status,category_name,category_slug")
        .eq("listing_id", listingId)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return ((data ?? []) as unknown as {
        id: string;
        value_cents: number;
        created_at: string;
        expires_at: string;
        status: string;
        category_name: string | null;
        category_slug: string | null;
      }[]).map((r) => ({
        id: r.id,
        value_cents: r.value_cents,
        created_at: r.created_at,
        expires_at: r.expires_at,
        status: r.status,
        category: r.category_slug ? { name: r.category_name ?? "", slug: r.category_slug } : null,
      }));
    },
  });
}

/** Heat + rank a listing holds on each board it appears on. */
export function listingPositionsQuery(listingId: string | undefined) {
  return queryOptions({
    queryKey: ["listing-positions", listingId],
    enabled: Boolean(listingId),
    queryFn: async () => {
      if (!listingId) return [];
      const { data: links, error } = await supabase
        .from("listing_categories")
        .select("category:categories(id,name,slug,minimum_boost_cents)")
        .eq("listing_id", listingId);
      if (error) throw error;
      const cats = ((links ?? []) as unknown as {
        category: { id: string; name: string; slug: string; minimum_boost_cents: number } | null;
      }[])
        .map((r) => r.category)
        .filter(Boolean) as { id: string; name: string; slug: string; minimum_boost_cents: number }[];
      if (!cats.length) return [];

      const { data: rows, error: boardError } = await supabase
        .from("category_board")
        .select("category_id,listing_id,active_heat_cents")
        .in(
          "category_id",
          cats.map((c) => c.id),
        )
        .order("active_heat_cents", { ascending: false });
      if (boardError) throw boardError;

      const board = (rows ?? []) as unknown as {
        category_id: string;
        listing_id: string;
        active_heat_cents: number;
      }[];

      return cats.map((c) => {
        const inCat = board.filter((r) => r.category_id === c.id);
        const index = inCat.findIndex((r) => r.listing_id === listingId);
        const own = inCat.find((r) => r.listing_id === listingId);
        return {
          category: c,
          rank: index >= 0 ? index + 1 : null,
          heat_cents: Number(own?.active_heat_cents ?? 0),
          competitors: inCat.length,
        };
      });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Account                                                             */
/* ------------------------------------------------------------------ */

export function myProfileQuery(userId: string | undefined) {
  return queryOptions({
    queryKey: ["my-profile", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id,display_name,handle,country_code,avatar_url,bio,website_url,referral_code")
        .eq("id", userId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function myBillingQuery(userId: string | undefined) {
  return queryOptions({
    queryKey: ["my-billing", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("billing_profiles")
        .select("*")
        .eq("user_id", userId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function myOrdersQuery(userId: string | undefined) {
  return queryOptions({
    queryKey: ["my-orders", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(
          "id,status,kind,currency,boost_amount_cents,credits_applied_cents,amount_due_cents,amount_paid_cents,invoice_number,receipt_number,stripe_payment_intent_id,paid_at,created_at,listing:listings(name,slug),category:categories(name,slug)",
        )
        .eq("user_id", userId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as {
        id: string;
        status: string;
        kind: string | null;
        currency: string;
        boost_amount_cents: number;
        credits_applied_cents: number;
        amount_due_cents: number;
        amount_paid_cents: number;
        invoice_number: string | null;
        receipt_number: string | null;
        stripe_payment_intent_id: string | null;
        paid_at: string | null;
        created_at: string;
        listing: { name: string; slug: string } | null;
        category: { name: string; slug: string } | null;
      }[];

    },
  });
}

export function myBoostsQuery(userId: string | undefined) {
  return queryOptions({
    queryKey: ["my-boosts", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc("my_boosts");
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id,
        value_cents: row.value_cents,
        status: row.status as string,
        starts_at: row.starts_at,
        expires_at: row.expires_at,
        listing: row.listing_name
          ? { name: row.listing_name, slug: row.listing_slug ?? "" }
          : null,
        category: row.category_name
          ? { name: row.category_name, slug: row.category_slug ?? "" }
          : null,
      }));
    },
  });
}

export function myReferralsQuery(userId: string | undefined) {
  return queryOptions({
    queryKey: ["my-referrals", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("referrals")
        .select("id,status,reward_cents,rewarded_at,created_at")
        .eq("referrer_user_id", userId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function myListingQuery(listingId: string | undefined) {
  return queryOptions({
    queryKey: ["my-listing", listingId],
    enabled: Boolean(listingId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("*")
        .eq("id", listingId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}
